/* eslint-disable no-param-reassign, no-underscore-dangle, consistent-return */
import axios, { AxiosHeaders, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import getAccessTokenFromCodeRefreshToken from 'src/utils/auth/getAccessTokenFromCodeRefreshToken';
import { getEnv } from 'src/utils/env.vite';
import {
  ERR_CODE_400,
  ERR_CODE_401,
  ERR_CODE_403,
  ERR_CODE_500,
  INTERVAL_TIME_120000,
} from '../constants/Common';
import { sendNotification } from '../UI/Notification';
import { markNavigationCancellation } from './requestCancellationState';

const LEGACY_GENESIS_BASE_URL = getEnv('VITE_APP_GEN_API_BASE_URL') ?? '';
const NEW_GENESIS_BASE_URL = getEnv('VITE_APP_NEW_GEN_API_BASE_URL') ?? '';
const NEW_GENESIS_ROUTE_PREFIX = '/new-genesis';

export const resolveGenesisBaseUrl = (pathname?: string | null) => {
  if (pathname?.startsWith(NEW_GENESIS_ROUTE_PREFIX)) {
    return NEW_GENESIS_BASE_URL || LEGACY_GENESIS_BASE_URL;
  }
  return LEGACY_GENESIS_BASE_URL;
};

const getCurrentBaseUrl = () => {
  if (globalThis.window === undefined) {
    return LEGACY_GENESIS_BASE_URL;
  }
  return resolveGenesisBaseUrl(globalThis.window.location.pathname);
};

export const axiosConfig = {
  baseURL: LEGACY_GENESIS_BASE_URL,
  timeout: INTERVAL_TIME_120000,
  headers: {},
};

const TWO = 2;
const ONE_SIX = 16;
const TOKEN_REFRESH_BUFFER_SECONDS = 300;

const CANCEL_REASON_NAVIGATION = 'Navigation';
const CANCEL_REASON_ROUTE_CHANGED = 'Route changed';

type ResponseType = 'json' | 'blob' | 'text';
type ARRAY_BUFFER = 'arraybuffer';

export const GenesisApi = axios.create(axiosConfig);

export const isRequestCanceled = (error: unknown): boolean => {
  if (axios.isCancel(error)) {
    return true;
  }

  const cancellationError = error as {
    name?: string;
    code?: string;
    message?: string;
  };

  return (
    cancellationError?.name === 'CanceledError' ||
    cancellationError?.code === 'ERR_CANCELED' ||
    cancellationError?.message === 'canceled'
  );
};

/**
 * Global registry of ALL pending requests done via GenesisApi.
 * This lets us abort everything on navigation without changing call sites.
 */
type TrackedRequest = {
  controller: AbortController;
  timeoutId: ReturnType<typeof setTimeout>;
  routePath: string;
};

const pendingRequests = new Map<string, TrackedRequest>();
let inFlightRefreshPromise: Promise<string> | null = null;

const makeRequestKey = (config: InternalAxiosRequestConfig) => {
  const method = (config.method ?? 'get').toUpperCase();
  const url = config.url ?? '';
  // Ensure uniqueness even for identical URL/method fired multiple times
  const randomId = crypto
    .getRandomValues(new Uint8Array(ONE_SIX))
    .reduce((acc, byte) => acc + byte.toString(ONE_SIX).padStart(TWO, '0'), '');
  return `${method}::${url}::${Date.now()}::${randomId}`;
};

export const shouldRefreshAccessToken = () => {
  const expiryTime = sessionStorage.getItem('expiry_time');
  if (!expiryTime) {
    return false;
  }

  const exp = dayjs.unix(Number.parseInt(expiryTime, 10));
  const remainingSeconds = exp.diff(dayjs(), 'seconds');
  return remainingSeconds < TOKEN_REFRESH_BUFFER_SECONDS;
};

const refreshAccessToken = async (forceRefresh = false) => {
  // Always coalesce into an in-flight refresh first so concurrent callers
  // wait on one network round-trip regardless of forceRefresh/expiry state.
  if (inFlightRefreshPromise) {
    return inFlightRefreshPromise;
  }

  const currentAccessToken = sessionStorage.getItem('access_token') ?? '';
  if (!forceRefresh && !shouldRefreshAccessToken()) {
    return currentAccessToken;
  }

  inFlightRefreshPromise = (async () => {
    const refreshToken = sessionStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('Refresh token not available');
    }

    const { accessToken, error } = await getAccessTokenFromCodeRefreshToken(refreshToken);
    if (error || !accessToken) {
      throw new Error(error ?? 'Token refresh failed');
    }

    return accessToken;
  })();

  try {
    return await inFlightRefreshPromise;
  } finally {
    inFlightRefreshPromise = null;
  }
};

export const ensureFreshAccessToken = async () => {
  try {
    return await refreshAccessToken();
  } catch {
    return sessionStorage.getItem('access_token') ?? '';
  }
};

export const cancelAllPendingRequests = (reason = CANCEL_REASON_NAVIGATION) => {
  if (reason === CANCEL_REASON_NAVIGATION || reason === CANCEL_REASON_ROUTE_CHANGED) {
    markNavigationCancellation();
  }

  pendingRequests.forEach(({ controller, timeoutId }) => {
    clearTimeout(timeoutId);
    controller.abort(reason);
  });
  pendingRequests.clear();
};

export const cancelPendingRequestsForRoute = (
  routePath: string,
  reason = CANCEL_REASON_ROUTE_CHANGED,
) => {
  if (reason === CANCEL_REASON_NAVIGATION || reason === CANCEL_REASON_ROUTE_CHANGED) {
    markNavigationCancellation();
  }

  pendingRequests.forEach((trackedRequest, requestKey) => {
    if (trackedRequest.routePath === routePath) {
      clearTimeout(trackedRequest.timeoutId);
      trackedRequest.controller.abort(reason);
      pendingRequests.delete(requestKey);
    }
  });
};

// Attach auth headers + AbortController + tracking for every request
GenesisApi.interceptors.request.use(async (config) => {
  const towerPCUserName = sessionStorage.getItem('tower-pc-user');
  let token = sessionStorage.getItem('access_token') ?? '';

  // Route-aware base URL selection:
  // /new-genesis/* uses VITE_APP_NEW_GEN_API_BASE_URL, everything else uses legacy Genesis base URL.
  config.baseURL = getCurrentBaseUrl();

  if (shouldRefreshAccessToken()) {
    token = await ensureFreshAccessToken();
  }

  config.headers.Authorization = `Bearer ${token}`;
  if (towerPCUserName) {
    config.headers.TowerPcUserName = towerPCUserName;
  }

  // If caller already provided a signal, respect it; otherwise attach ours.
  // (This keeps compatibility if some specific call wants custom cancellation.)
  const controller = new AbortController();
  config.signal ??= controller.signal;

  const requestKey = makeRequestKey(config);
  const routePath =
    typeof globalThis.window === 'undefined' ? '' : globalThis.window.location.pathname;
  (config as any).__requestKey = requestKey;

  const timeoutId = setTimeout(() => {
    controller.abort(`Connection timeout of ${axiosConfig.timeout}ms.`);
    pendingRequests.delete(requestKey);
  }, axiosConfig.timeout);

  pendingRequests.set(requestKey, { controller, timeoutId, routePath });

  return config;
});

// Cleanup request tracking on response or error
GenesisApi.interceptors.response.use(
  (response) => {
    const key = (response.config as any).__requestKey as string | undefined;
    if (key) {
      const tracked = pendingRequests.get(key);
      if (tracked) {
        clearTimeout(tracked.timeoutId);
      }
      pendingRequests.delete(key);
    }
    return response;
  },
  (err) => {
    const key = err?.config?.__requestKey as string | undefined;
    if (key) {
      const tracked = pendingRequests.get(key);
      if (tracked) {
        clearTimeout(tracked.timeoutId);
      }
      pendingRequests.delete(key);
    }

    // IMPORTANT: do not treat aborts as errors
    if (isRequestCanceled(err)) {
      // Let callers receive a cancellation rejection so loading states can settle.
      return Promise.reject(err);
    }

    return Promise.reject(err instanceof Error ? err : new Error(String(err)));
  },
);
export const get = async (
  path: string,
  header?: {
    responseType?: ResponseType | ARRAY_BUFFER;
    signal?: AbortSignal;
  },
): Promise<AxiosResponse> => {
  return GenesisApi.get(path, {
    responseType: header?.responseType ?? 'json',
    signal: header?.signal,
  });
};

export const post = async (path: string, body: Record<string, unknown>): Promise<AxiosResponse> => {
  return GenesisApi.post(path, body);
};
export const postFormData = async (url: string, data: FormData) => {
  return GenesisApi.post(url, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};
export const patch = async (
  path: string,
  body: Record<string, unknown>,
): Promise<AxiosResponse> => {
  return GenesisApi.patch(path, body);
};

export const put = async (path: string, body: Record<string, unknown>): Promise<AxiosResponse> => {
  return GenesisApi.put(path, body);
};

export const deleteApi = async (
  path: string,
  body: Record<string, unknown>,
): Promise<AxiosResponse> => {
  return GenesisApi.delete(path, { data: body });
};

export const AxiosInterceptor = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSet, setIsSet] = useState(false);
  const prevPath = useRef(location.pathname);

  // Cancel all pending requests when route changes
  useEffect(() => {
    if (prevPath.current !== location.pathname) {
      cancelPendingRequestsForRoute(prevPath.current, CANCEL_REASON_ROUTE_CHANGED);
      prevPath.current = location.pathname;
    }
  }, [location.pathname]);

  useEffect(() => {
    const resInterceptor = (response) => response;

    const handleTokenRefresh = async (err) => {
      const originalRequest = err?.config as
        | (InternalAxiosRequestConfig & { __isRetryRequest?: boolean })
        | undefined;

      if (!originalRequest || originalRequest.__isRetryRequest) {
        return null;
      }

      try {
        originalRequest.__isRetryRequest = true;
        const refreshedToken = await refreshAccessToken(true);
        if (!originalRequest.headers) {
          originalRequest.headers = new AxiosHeaders();
        }
        originalRequest.headers.Authorization = 'Bearer '.concat(refreshedToken);
        return GenesisApi(originalRequest);
      } catch {
        navigate('/unauthorized-application');
        return null;
      }
    };

    const handleErrorNotification = (status, data) => {
      if (status === ERR_CODE_500) {
        if (typeof data === 'string') {
          sendNotification({ type: 'error', message: data });
        } else if (typeof data?.title === 'string') {
          sendNotification({ type: 'error', message: data.title });
        }
      } else if (status === ERR_CODE_400) {
        if (typeof data?.title === 'string') {
          sendNotification({ type: 'error', message: data.title });
        } else if (typeof data === 'string') {
          sendNotification({ type: 'error', message: data });
        }
      } else if (status === ERR_CODE_403) {
        navigate('/forbidden');
      }
    };

    const errInterceptor = async (err) => {
      // Swallow cancellations globally (expected on navigation)
      if (isRequestCanceled(err)) {
        throw err;
      }

      const error = err?.response || err;
      if (error?.status === ERR_CODE_401) {
        const result = await handleTokenRefresh(err);
        if (result) return result;
      }

      handleErrorNotification(error?.status, error?.data);

      return Promise.reject(new Error(error?.statusText || 'An error occurred'));
    };

    const id = GenesisApi.interceptors.response.use(resInterceptor, errInterceptor);
    setIsSet(true);

    // Prevent duplicate interceptors (StrictMode / HMR)
    return () => {
      GenesisApi.interceptors.response.eject(id);
    };
  }, [navigate]);

  return isSet && children;
};
